import React, { useEffect, useState } from "react";
import { proposals, exporter, getStoredUser } from "@/lib/api";

const ICON = { pass: "✅", warn: "⚠️", fail: "❌" };
const COLOR = { pass: "var(--success)", warn: "var(--warning)", fail: "var(--danger)" };
const SNAP_LABELS = [
  { key: "pink", label: "Pink Team" },
  { key: "red", label: "Red Team" },
  { key: "gold", label: "Gold Team" },
  { key: "final", label: "Final" },
];

// Rendered as a Response Workspace tab panel (same role as the AI Response
// Editor / Team Assignments panels) — takes the already-loaded proposal from
// the parent rather than resolving its own, so it can never show a different
// proposal than the rest of the workspace. Checklist/preflight/snapshots are
// still fetched here since they're not part of the parent's proposal object.
export default function SubmissionTab({ proposal }) {
  const [ready, setReady] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [busy, setBusy] = useState("");
  const [blockModal, setBlockModal] = useState(null); // { kind }
  const [justification, setJustification] = useState("");
  const user = getStoredUser();
  const isPM = ["Administrator", "Proposal Manager"].includes(user?.role);

  const reload = async (pid) => {
    setChecklist(await proposals.checklist(pid));
    setPreflight(await proposals.preflight(pid));
    setSnapshots(await proposals.snapshots(pid));
  };

  useEffect(() => {
    setReady(false);
    reload(proposal.id).finally(() => setReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.id]);

  if (!ready || !checklist) {
    return (
      <div className="center-load" data-testid="submission-panel"><span className="spinner" /> Loading…</div>
    );
  }

  const runExport = (kind, opts = {}) => {
    const fns = {
      docx: () => exporter.docx(proposal.id, opts),
      pdf: () => exporter.pdf(proposal.id, opts),
      matrix: () => exporter.matrixXlsx(proposal.id),
      package: () => exporter.packageZip(proposal.id, opts),
    };
    return fns[kind]();
  };

  const doExport = async (kind) => {
    // Matrix is always allowed (no compliance gate)
    if (kind !== "matrix" && preflight?.blocked) {
      setJustification("");
      setBlockModal({ kind });
      return;
    }
    setBusy(kind);
    try {
      await runExport(kind);
    } catch (e) {
      window.alert(e?.response?.data?.detail?.message || "Export failed");
    } finally {
      setBusy("");
    }
  };

  const confirmOverride = async () => {
    if (!blockModal) return;
    setBusy(blockModal.kind);
    try {
      await runExport(blockModal.kind, { override: true, justification });
      setBlockModal(null);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Override failed (Proposal Manager role required)");
    } finally {
      setBusy("");
    }
  };

  const capture = async (label) => {
    setBusy("snap-" + label);
    try {
      await proposals.createSnapshot(proposal.id, label);
      await reload(proposal.id);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Snapshot failed (Proposal Manager role required)");
    } finally {
      setBusy("");
    }
  };

  const issuesCount = checklist.issues?.length || 0;
  const blockedCount = preflight?.incomplete_p0?.length || 0;
  const snapBy = (k) => snapshots.find((s) => s.snapshot_type === k);

  return (
    <div className="flex flex-col" style={{ gap: 24 }} data-testid="submission-panel">
      <div className="page-header">
        <div>
          <div className="page-title">Submission Builder</div>
          <div className="page-subtitle">{proposal.title} · Final pre-submission checklist</div>
        </div>
        <div className="flex gap-8">
          <button className="btn btn-ghost btn-sm" onClick={() => doExport("pdf")} disabled={busy} data-testid="preview-btn">
            👁 Preview (PDF)
          </button>
          <button className="btn btn-success btn-sm" onClick={() => doExport("package")} disabled={busy} data-testid="submit-btn">
            🚀 Build Submission Package
          </button>
        </div>
      </div>

      {preflight?.blocked && (
        <div className="alert alert-warn" data-testid="preflight-alert" style={{ borderColor: "var(--danger)" }}>
          <span>🚫</span>
          <span>
            <strong>Export gated:</strong> {blockedCount} P0 requirement{blockedCount > 1 ? "s are" : " is"} incomplete.
            {isPM ? " As Proposal Manager you may override with a documented justification." : " A Proposal Manager must override to proceed."}
          </span>
        </div>
      )}

      {issuesCount > 0 && !preflight?.blocked && (
        <div className="alert alert-warn" data-testid="submission-alert">
          <span>⚠️</span>
          <span>
            <strong>{issuesCount} item{issuesCount > 1 ? "s" : ""} require attention</strong> before this package is submission-ready.
          </span>
        </div>
      )}

      <div className="split-340">
        <div className="flex flex-col" style={{ gap: 16 }}>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Pre-Submission Checklist</span>
              <span className={`pill ${issuesCount ? "pill-orange" : "pill-green"}`}>
                {issuesCount ? `${issuesCount} Issues` : "All Clear"}
              </span>
            </div>
            <div style={{ padding: "4px 20px" }}>
              {checklist.items.map((it, i) => (
                <div className="checklist-item" key={i} data-testid={`check-${i}`}>
                  <span className="check-icon">{ICON[it.status] || ICON.warn}</span>
                  <span className="check-label" style={{ color: it.status === "warn" ? "var(--warning)" : undefined }}>
                    {it.label}
                  </span>
                  <span className="check-status" style={{ color: COLOR[it.status] || COLOR.warn }}>
                    {it.status === "pass" ? "PASS" : it.status === "warn" ? "ACTION NEEDED" : "FAIL"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Export Package</span></div>
            <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
              <ExportTile icon="📝" title="Proposal (Word)" sub={`DOCX · ${checklist.total_pages} pages`}
                onClick={() => doExport("docx")} busy={busy === "docx"} testid="export-docx" />
              <ExportTile icon="📄" title="Proposal (PDF)" sub="PDF · archival"
                onClick={() => doExport("pdf")} busy={busy === "pdf"} testid="export-pdf" />
              <ExportTile icon="📊" title="Compliance Matrix" sub={`XLSX · ${proposal.requirements.length} rows`}
                onClick={() => doExport("matrix")} busy={busy === "matrix"} testid="export-matrix-xlsx" />
              <ExportTile icon="📦" title="Full Package ZIP" sub="Multi-volume + matrix" primary
                onClick={() => doExport("package")} busy={busy === "package"} testid="download-package-btn" />
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Color Team Review Snapshots</span>
              {!isPM && <span className="pill pill-gray" title="Proposal Manager role required">PM only</span>}
            </div>
            <div style={{ padding: "14px 20px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
              {SNAP_LABELS.map(({ key, label }) => {
                const snap = snapBy(key);
                return (
                  <div key={key} style={{ padding: 12, border: "1.5px solid var(--border)", borderRadius: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, textTransform: "capitalize" }}>{label}</div>
                    {snap ? (
                      <>
                        <div style={{ fontSize: 10, color: "var(--text-muted)", margin: "4px 0" }}>
                          v{snap.version_number} · {new Date(snap.created_at).toLocaleDateString()}
                        </div>
                        <button className="btn btn-ghost btn-sm" style={{ width: "100%", justifyContent: "center" }}
                          onClick={() => proposals.snapshotPdf(proposal.id, snap.id)} data-testid={`snap-dl-${key}`}>
                          ↓ PDF
                        </button>
                      </>
                    ) : (
                      <button className="btn btn-outline btn-sm" style={{ width: "100%", justifyContent: "center", marginTop: 8 }}
                        onClick={() => capture(key)} disabled={!isPM || busy === "snap-" + key} data-testid={`snap-capture-${key}`}>
                        {busy === "snap-" + key ? <span className="spinner" /> : "Capture"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-col" style={{ gap: 16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Submission Timeline</span></div>
            <div className="card-body">
              <div className="timeline">
                <div className="tl-item">
                  <div className="tl-left"><div className="tl-dot done" /><div className="tl-line" /></div>
                  <div className="tl-content">
                    <div className="tl-title">Solicitation Ingested</div>
                    <div className="tl-desc">{proposal.requirements.length} requirements auto-extracted</div>
                    <div className="tl-date">{new Date(proposal.created_at).toDateString()}</div>
                  </div>
                </div>
                <div className="tl-item">
                  <div className="tl-left"><div className="tl-dot active" /><div className="tl-line" /></div>
                  <div className="tl-content">
                    <div className="tl-title" style={{ color: "var(--primary)" }}>Drafting — In Progress</div>
                    <div className="tl-desc">{checklist.sections_complete}/{checklist.sections_total} sections complete</div>
                  </div>
                </div>
                <div className="tl-item">
                  <div className="tl-left"><div className={`tl-dot ${snapshots.length ? "done" : "pending"}`} /><div className="tl-line" /></div>
                  <div className="tl-content">
                    <div className="tl-title" style={{ color: snapshots.length ? undefined : "var(--text-muted)" }}>Color Team Reviews</div>
                    <div className="tl-desc">
                      {SNAP_LABELS.filter((s) => snapBy(s.key)).map((s) => s.label.split(" ")[0]).join(" → ") || "Pink → Red → Gold"}
                    </div>
                  </div>
                </div>
                <div className="tl-item">
                  <div className="tl-left"><div className="tl-dot pending" /></div>
                  <div className="tl-content">
                    <div className="tl-title" style={{ color: "var(--text-muted)" }}>Submit to SAM.gov</div>
                    <div className="tl-desc">Deadline: {proposal.due_date ? new Date(proposal.due_date).toDateString() : "—"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Package Summary</span></div>
            <div style={{ padding: "14px 20px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                <div className="flex justify-between"><span style={{ color: "var(--text-secondary)" }}>Total Pages</span><span className="font-700">{checklist.total_pages} / 50 max</span></div>
                <div className="flex justify-between"><span style={{ color: "var(--text-secondary)" }}>Requirements Covered</span><span className="font-700" style={{ color: "var(--success)" }}>{checklist.requirements_covered}/{checklist.requirements_total} ({checklist.coverage_pct}%)</span></div>
                <div className="flex justify-between"><span style={{ color: "var(--text-secondary)" }}>Sections Complete</span><span className="font-700">{checklist.sections_complete}/{checklist.sections_total}</span></div>
                <div className="flex justify-between"><span style={{ color: "var(--text-secondary)" }}>Word Count</span><span className="font-700">{checklist.total_words.toLocaleString()} words</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {blockModal && (
        <div className="modal-backdrop" onClick={() => setBlockModal(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="override-modal">
            <h3>Export blocked — incomplete P0 requirements</h3>
            <div className="modal-sub">
              The following must-have (P0) requirements are not yet complete. Exporting now requires a
              documented Proposal Manager override.
            </div>
            <div style={{ maxHeight: 180, overflow: "auto", margin: "8px 0", fontSize: 12 }}>
              {(preflight?.incomplete_p0 || []).map((r) => (
                <div key={r.id} style={{ padding: "4px 0", borderBottom: "1px solid var(--border)" }}>
                  <strong>{r.id}</strong> · {r.status} — {r.text}
                </div>
              ))}
            </div>
            {isPM ? (
              <>
                <label>Override justification *</label>
                <textarea rows={3} value={justification} onChange={(e) => setJustification(e.target.value)}
                  placeholder="Why is it acceptable to export with these P0 gaps?" data-testid="override-justification" />
              </>
            ) : (
              <div className="alert alert-warn" style={{ marginTop: 8 }}>
                <span>⚠️</span><span>Only a Proposal Manager can override this gate.</span>
              </div>
            )}
            <div className="flex gap-8" style={{ marginTop: 16, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setBlockModal(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={confirmOverride}
                disabled={!isPM || justification.trim().length < 5 || busy} data-testid="override-confirm-btn">
                Override &amp; Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExportTile({ icon, title, sub, onClick, busy, primary, testid }) {
  return (
    <div style={{
      padding: 14,
      border: `1.5px solid ${primary ? "var(--primary)" : "var(--border)"}`,
      borderRadius: 8,
      background: primary ? "var(--primary-light)" : undefined,
    }}>
      <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: primary ? "var(--primary)" : undefined }}>{title}</div>
      <div style={{ fontSize: 11, color: primary ? "var(--primary)" : "var(--text-muted)", opacity: primary ? 0.8 : 1 }}>{sub}</div>
      <button
        className={`btn ${primary ? "btn-primary" : "btn-ghost"} btn-sm`}
        style={{ marginTop: 8, width: "100%", justifyContent: "center" }}
        onClick={onClick}
        disabled={busy}
        data-testid={testid}
      >
        {busy ? <span className="spinner" /> : primary ? "Download All" : "Export"}
      </button>
    </div>
  );
}
